import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@jem-hr/jem-ui";
import { ArrowLeft, CheckCircle2, Settings, X } from "lucide-react";
import { SiteConfigCard, SiteFieldKey } from "../components/SiteConfigCard";
import {
  RosterConfig,
  RosterConfigSection,
  RosterAccordionSummary,
  SiteDefaults,
} from "../components/RosterConfigSection";

const SITE_LABELS: Record<string, string> = {
  newtown: "Newtown Site",
  sandton: "Sandton Site",
  "cape-town": "Cape Town Office",
};

/* ============================================================
 * Mock data — 1 Site, 3 Rosters demonstrating different states.
 * ============================================================ */

const INITIAL_SITE: SiteDefaults & {
  address: string;
} = {
  siteName: "Newtown Site",
  address: "12 Henry Nxumalo Street, Newtown, Johannesburg",
  latitude: "-26.20227",
  longitude: "28.04363",
  geofenceRadius: "150",
  lateArrivalThreshold: "15",
};

const INITIAL_ROSTERS: RosterConfig[] = [
  {
    id: "roster-day",
    name: "Main Building",
    shiftWindow: "Day · Night",
    assignedHeadcount: 50,
    requiredHeadcount: "40",
    shiftTypes: [
      {
        id: "shift-day",
        type: "Day",
        window: "06:00–14:00",
        requiredHeadcount: "40",
        assignedHeadcount: 38,
      },
      {
        id: "shift-night",
        type: "Night",
        window: "22:00–06:00",
        requiredHeadcount: "",
        assignedHeadcount: 12,
      },
    ],
    lateArrivalSource: "site",
    useSiteGeofence: true,
    dirty: false,
  },
  {
    id: "roster-warehouse",
    name: "Warehouse Shift",
    shiftWindow: "07:00–15:00",
    assignedHeadcount: 22,
    requiredHeadcount: "22",
    lateArrivalSource: "override",
    lateArrivalOverrideValue: "10",
    useSiteGeofence: false,
    geofenceAddress: "8 Quinn Street, Newtown, Johannesburg",
    geofenceLatitude: "-26.20389",
    geofenceLongitude: "28.04891",
    geofenceRadius: "80",
    dirty: false,
  },
  {
    id: "roster-loading-dock",
    name: "Loading Dock",
    shiftWindow: "08:00–17:00",
    assignedHeadcount: 9,
    requiredHeadcount: "",
    lateArrivalSource: "site",
    useSiteGeofence: true,
    dirty: false,
  },
];

/* ============================================================
 * URL-variant parsing — drives all 7 demonstrable states.
 * ============================================================ */

type Variant =
  | "default"
  | "site_edit_pending"
  | "site_edit_saved"
  | "geofence_overridden"
  | "geofence_revert_confirm"
  | "headcount_below"
  | "audit_drawer_open";

function readVariant(): Variant {
  if (typeof window === "undefined") return "default";
  const v = new URLSearchParams(window.location.search).get("variant");
  switch (v) {
    case "site_edit_pending":
    case "site_edit_saved":
    case "geofence_overridden":
    case "geofence_revert_confirm":
    case "headcount_below":
    case "audit_drawer_open":
      return v;
    default:
      return "default";
  }
}

function readDeepLinkRosterId(): string | null {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("roster");
}

function readDevMode(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("dev") === "1";
}

/* ============================================================
 * Mock audit history (ENG-4311 User Story 3).
 * ============================================================ */

interface AuditEntry {
  id: string;
  timestamp: string;
  user: string;
  field: string;
  oldValue: string;
  newValue: string;
  updatedRosters: string[];
  skippedRosters: string[];
}

const MOCK_AUDIT: AuditEntry[] = [
  {
    id: "audit-1",
    timestamp: "2026-06-15 14:22",
    user: "Amber Jones",
    field: "Late arrival threshold",
    oldValue: "20 min",
    newValue: "15 min",
    updatedRosters: ["Main Building", "Loading Dock"],
    skippedRosters: ["Warehouse Shift"],
  },
  {
    id: "audit-2",
    timestamp: "2026-05-30 09:08",
    user: "Dawie van der Westhuizen",
    field: "Geofence radius",
    oldValue: "200m",
    newValue: "150m",
    updatedRosters: ["Main Building", "Loading Dock"],
    skippedRosters: ["Warehouse Shift"],
  },
  {
    id: "audit-3",
    timestamp: "2026-05-12 11:45",
    user: "Amber Jones",
    field: "Warehouse Shift — geofence override",
    oldValue: "(inheriting)",
    newValue: "Custom (-26.20389, 28.04891, 80m)",
    updatedRosters: [],
    skippedRosters: [],
  },
];

/* ============================================================
 * Page component.
 * ============================================================ */

export interface SiteConfigurationProps {
  onNavigate?: (key: string) => void;
}

export function SiteConfiguration({ onNavigate }: SiteConfigurationProps = {}) {
  const [variant, setVariant] = useState<Variant>(readVariant());
  const [site, setSite] = useState(INITIAL_SITE);
  const [savedSite, setSavedSite] = useState(INITIAL_SITE);
  const [rosters, setRosters] = useState<RosterConfig[]>(INITIAL_ROSTERS);
  const [siteDirty, setSiteDirty] = useState(false);
  const [siteDirtyField, setSiteDirtyField] = useState<SiteFieldKey | null>(null);
  const [postSaveAlert, setPostSaveAlert] = useState<{
    field: string;
    oldValue: string;
    newValue: string;
    updated: string[];
    skipped: string[];
  } | null>(null);
  const [auditDrawerOpen, setAuditDrawerOpen] = useState(false);
  const [pendingRevertId, setPendingRevertId] = useState<string | null>(null);
  const [pendingMoveRosterId, setPendingMoveRosterId] = useState<string | null>(
    null
  );
  const [pendingMoveDestination, setPendingMoveDestination] = useState<string>("");
  const [movedAlert, setMovedAlert] = useState<{
    rosterName: string;
    destination: string;
  } | null>(null);

  /* -------------- Variant bootstrap (URL-driven demo states) -------------- */
  useEffect(() => {
    // Re-read on mount.
    setVariant(readVariant());
  }, []);

  useEffect(() => {
    if (variant === "site_edit_pending") {
      setSite((s) => ({ ...s, lateArrivalThreshold: "10" }));
      setSiteDirty(true);
      setSiteDirtyField("lateArrivalThreshold");
    } else if (variant === "site_edit_saved") {
      setPostSaveAlert({
        field: "Late arrival threshold",
        oldValue: "15 min",
        newValue: "10 min",
        updated: ["Main Building", "Loading Dock"],
        skipped: ["Warehouse Shift"],
      });
    } else if (variant === "geofence_overridden") {
      // Default mock data already has Warehouse Shift overridden — nothing to do.
    } else if (variant === "geofence_revert_confirm") {
      setRosters((rs) =>
        rs.map((r) =>
          r.id === "roster-warehouse" ? { ...r, dirty: true } : r
        )
      );
      setPendingRevertId("roster-warehouse");
    } else if (variant === "headcount_below") {
      // Make the Day shift of Main Building below target (req=40, assigned=30).
      setRosters((rs) =>
        rs.map((r) => {
          if (r.id !== "roster-day" || !r.shiftTypes) return r;
          return {
            ...r,
            shiftTypes: r.shiftTypes.map((s) =>
              s.id === "shift-day"
                ? { ...s, requiredHeadcount: "40", assignedHeadcount: 30 }
                : s
            ),
          };
        })
      );
    } else if (variant === "audit_drawer_open") {
      setAuditDrawerOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  /* -------------- Derived values -------------- */
  const derivedRequiredHeadcount = useMemo(() => {
    return rosters.reduce((acc, r) => {
      if (r.shiftTypes && r.shiftTypes.length > 0) {
        return (
          acc +
          r.shiftTypes.reduce((sAcc, s) => {
            const req = s.requiredHeadcount ? Number(s.requiredHeadcount) : NaN;
            return sAcc + (Number.isFinite(req) ? req : s.assignedHeadcount);
          }, 0)
        );
      }
      const req = r.requiredHeadcount ? Number(r.requiredHeadcount) : NaN;
      return acc + (Number.isFinite(req) ? req : r.assignedHeadcount);
    }, 0);
  }, [rosters]);

  const rostersInFallback = useMemo(
    () =>
      rosters.filter((r) => {
        if (r.shiftTypes && r.shiftTypes.length > 0) {
          return r.shiftTypes.some((s) => !s.requiredHeadcount);
        }
        return !r.requiredHeadcount;
      }).length,
    [rosters]
  );

  /* -------------- Site change semantics -------------- */
  const cascadeFieldLabel: Record<SiteFieldKey, string> = {
    siteName: "Site name",
    address: "Address",
    latitude: "Latitude",
    longitude: "Longitude",
    geofenceRadius: "Geofence radius",
    lateArrivalThreshold: "Late arrival threshold",
  };

  function handleSiteChange(field: SiteFieldKey, value: string) {
    setSite((s) => ({ ...s, [field]: value }));
    if (value !== savedSite[field as keyof typeof savedSite]) {
      setSiteDirty(true);
      setSiteDirtyField(field);
    }
  }

  function discardSiteChanges() {
    setSite(savedSite);
    setSiteDirty(false);
    setSiteDirtyField(null);
  }

  function saveSiteChanges() {
    setTimeout(() => {
      // Figure out cascade targets based on the dirty field.
      const field = siteDirtyField;
      let updated: RosterConfig[] = [];
      let skipped: RosterConfig[] = [];
      let oldValue = "";
      let newValue = "";

      if (field === "lateArrivalThreshold") {
        oldValue = `${savedSite.lateArrivalThreshold} min`;
        newValue = `${site.lateArrivalThreshold} min`;
        updated = rosters.filter((r) => r.lateArrivalSource === "site");
        skipped = rosters.filter((r) => r.lateArrivalSource === "override");
      } else if (
        field === "latitude" ||
        field === "longitude" ||
        field === "geofenceRadius"
      ) {
        oldValue = String(savedSite[field as keyof typeof savedSite]);
        newValue = String(site[field as keyof typeof site]);
        updated = rosters.filter((r) => r.useSiteGeofence);
        skipped = rosters.filter((r) => !r.useSiteGeofence);
      }

      setSavedSite(site);
      setSiteDirty(false);
      setPostSaveAlert({
        field: field ? cascadeFieldLabel[field] : "Site",
        oldValue,
        newValue,
        updated: updated.map((r) => r.name),
        skipped: skipped.map((r) => r.name),
      });
      setSiteDirtyField(null);
    }, 400);
  }

  /* -------------- Roster change semantics -------------- */
  function handleRosterChange(id: string, patch: Partial<RosterConfig>) {
    setRosters((rs) => rs.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  function handleRevertField(id: string, field: "lateArrival") {
    if (field === "lateArrival") {
      handleRosterChange(id, {
        lateArrivalSource: "site",
        lateArrivalOverrideValue: undefined,
        dirty: true,
      });
    }
  }

  function handleToggleGeofence(id: string, useSite: boolean) {
    const roster = rosters.find((r) => r.id === id);
    if (!roster) return;

    // Off → On with override values present: confirm.
    if (
      useSite &&
      !roster.useSiteGeofence &&
      (roster.geofenceAddress ||
        roster.geofenceLatitude ||
        roster.geofenceLongitude ||
        roster.geofenceRadius)
    ) {
      setPendingRevertId(id);
      return;
    }

    // Off → On with no values, or On → Off.
    if (useSite) {
      handleRosterChange(id, {
        useSiteGeofence: true,
        geofenceAddress: undefined,
        geofenceLatitude: undefined,
        geofenceLongitude: undefined,
        geofenceRadius: undefined,
        dirty: true,
      });
    } else {
      // Switching to override — seed with the current Site values.
      handleRosterChange(id, {
        useSiteGeofence: false,
        geofenceAddress: site.address,
        geofenceLatitude: site.latitude,
        geofenceLongitude: site.longitude,
        geofenceRadius: site.geofenceRadius,
        dirty: true,
      });
    }
  }

  function confirmRevertGeofence() {
    if (!pendingRevertId) return;
    handleRosterChange(pendingRevertId, {
      useSiteGeofence: true,
      geofenceAddress: undefined,
      geofenceLatitude: undefined,
      geofenceLongitude: undefined,
      geofenceRadius: undefined,
      dirty: true,
    });
    setPendingRevertId(null);
  }

  function saveRoster(id: string) {
    handleRosterChange(id, { dirty: false });
  }

  function discardRoster(id: string) {
    // Restore from initial mock — V1 prototype only persists in-memory.
    const original = INITIAL_ROSTERS.find((r) => r.id === id);
    if (original) {
      setRosters((rs) => rs.map((r) => (r.id === id ? original : r)));
    }
  }

  /* -------------- Roster movement between Sites -------------- */
  const movingRoster = useMemo(
    () => rosters.find((r) => r.id === pendingMoveRosterId) ?? null,
    [rosters, pendingMoveRosterId]
  );

  function openMoveDialog(rosterId: string) {
    setPendingMoveRosterId(rosterId);
    setPendingMoveDestination("");
  }

  function closeMoveDialog() {
    setPendingMoveRosterId(null);
    setPendingMoveDestination("");
  }

  function confirmMove() {
    if (!pendingMoveRosterId || !pendingMoveDestination) return;
    const moved = rosters.find((r) => r.id === pendingMoveRosterId);
    if (!moved) return;
    const destinationLabel =
      SITE_LABELS[pendingMoveDestination] ?? pendingMoveDestination;
    setRosters((rs) => rs.filter((r) => r.id !== pendingMoveRosterId));
    setMovedAlert({ rosterName: moved.name, destination: destinationLabel });
    closeMoveDialog();
  }

  /* -------------- Render -------------- */
  return (
    <>
      <div
        className="-mx-xl -mt-lg px-xl py-lg mb-md"
        style={{ backgroundColor: "rgba(237,244,251,0.5)" }}
      >
        <Button
          variant="ghost"
          size="sm"
          className="mb-xs -ml-xs"
          leftIcon={<ArrowLeft size={14} />}
          onClick={() => onNavigate?.("site-configuration")}
        >
          Back to Sites &amp; Regions
        </Button>
        <h1 className="font-heading text-2xl font-semibold leading-36 text-greyscale-text-title">
          Site Configuration
        </h1>
        <p className="mt-xxs text-sm text-greyscale-text-body max-w-[760px]">
          Each Roster on this Site uses these settings unless it has its own
          custom value. Site changes update every Roster that doesn&apos;t have
          a custom value.
        </p>
      </div>

      <div className="space-y-md max-w-[1100px] pb-[120px]">
        {/* Site picker — left-aligned at top of content, 50% width on desktop. */}
        <div className="space-y-xxs sm:w-1/2">
          <span className="text-xs text-greyscale-text-caption">Site</span>
          <Select value="newtown" onValueChange={() => {}}>
            <SelectTrigger>
              <SelectValue placeholder="Select a Site" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newtown">Newtown Site</SelectItem>
              <SelectItem value="sandton">Sandton Site</SelectItem>
              <SelectItem value="cape-town">Cape Town Office</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Variant switcher — dev-only, behind ?dev=1 */}
        {readDevMode() && (
          <div className="flex flex-wrap items-center gap-xs">
            <span className="text-xs text-greyscale-text-caption">
              Demo state:
            </span>
            {(
              [
                "default",
                "site_edit_pending",
                "site_edit_saved",
                "geofence_overridden",
                "geofence_revert_confirm",
                "headcount_below",
                "audit_drawer_open",
              ] as Variant[]
            ).map((v) => (
              <a
                key={v}
                href={v === "default" ? "?dev=1" : `?dev=1&variant=${v}`}
                className={
                  variant === v
                    ? "rounded-md bg-secondary-pink-100 px-xs py-xxxs text-xs font-semibold text-greyscale-text-title"
                    : "rounded-md px-xs py-xxxs text-xs text-greyscale-text-caption hover:bg-secondary-pink-50"
                }
              >
                {v}
              </a>
            ))}
          </div>
        )}

        {/* Roster moved success Alert */}
        {movedAlert && (
          <Alert variant="success" className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-xs top-xs h-6 w-6"
              aria-label="Dismiss"
              onClick={() => setMovedAlert(null)}
            >
              <X size={14} />
            </Button>
            <AlertTitle icon={<CheckCircle2 size={16} />}>
              Roster moved
            </AlertTitle>
            <AlertDescription>
              <p className="pr-lg">
                <strong>{movedAlert.rosterName}</strong> moved to{" "}
                <strong>{movedAlert.destination}</strong>. All settings reset to{" "}
                {movedAlert.destination}'s defaults — review and customise the
                Roster at its new Site as needed.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {/* Post-save result Alert */}
        {postSaveAlert && (
          <Alert variant="success" className="relative">
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-xs top-xs h-6 w-6"
              aria-label="Dismiss"
              onClick={() => setPostSaveAlert(null)}
            >
              <X size={14} />
            </Button>
            <AlertTitle icon={<CheckCircle2 size={16} />}>
              {postSaveAlert.field} saved
            </AlertTitle>
            <AlertDescription>
              <p className="mb-xs pr-lg">
                Changed from <strong>{postSaveAlert.oldValue}</strong> to{" "}
                <strong>{postSaveAlert.newValue}</strong>. Updated{" "}
                <strong>{postSaveAlert.updated.length}</strong> Roster
                {postSaveAlert.updated.length === 1 ? "" : "s"};{" "}
                <strong>{postSaveAlert.skipped.length}</strong> kept their
                custom value.
              </p>
              {postSaveAlert.updated.length > 0 && (
                <p className="text-xs">
                  <span className="font-semibold">Updated:</span>{" "}
                  {postSaveAlert.updated.join(", ")}.
                </p>
              )}
              {postSaveAlert.skipped.length > 0 && (
                <p className="text-xs">
                  <span className="font-semibold">Kept custom:</span>{" "}
                  {postSaveAlert.skipped.join(", ")}.
                </p>
              )}
            </AlertDescription>
          </Alert>
        )}


        {/* ---------- Site card with embedded Save bar ---------- */}
        <SiteConfigCard
          siteName={site.siteName}
          address={site.address}
          latitude={site.latitude}
          longitude={site.longitude}
          geofenceRadius={site.geofenceRadius}
          lateArrivalThreshold={site.lateArrivalThreshold}
          forceEditing={siteDirty}
          onChange={handleSiteChange}
          onOpenHistory={() => setAuditDrawerOpen(true)}
          onSave={saveSiteChanges}
          onDiscard={discardSiteChanges}
        />

        {/* ---------- Rosters ---------- */}
        <section
          aria-label="Rosters"
          className="rounded-lg border border-greyscale-border bg-white"
        >
          <div className="flex flex-wrap items-center justify-between gap-sm p-md border-b border-greyscale-border">
            <div className="flex items-center gap-xs">
              <div className="rounded-md bg-secondary-pink-100 p-xxs">
                <Settings size={20} className="text-secondary-pink-900" />
              </div>
              <div>
                <h2 className="font-heading text-lg font-semibold text-greyscale-text-title">
                  Rosters on {site.siteName}
                </h2>
                <p className="text-xs text-greyscale-text-caption">
                  {rosters.length} Roster{rosters.length === 1 ? "" : "s"} ·{" "}
                  <strong>{derivedRequiredHeadcount}</strong> expected on site
                  {rostersInFallback > 0 && (
                    <>
                      {" "}
                      ·{" "}
                      <span className="text-greyscale-text-caption">
                        {rostersInFallback} using assigned headcount as fallback
                      </span>
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>

          <Accordion
            type="multiple"
            defaultValue={(() => {
              const deepLinkId = readDeepLinkRosterId();
              if (deepLinkId && rosters.some((r) => r.id === deepLinkId)) {
                return [deepLinkId];
              }
              if (variant === "geofence_overridden") return ["roster-warehouse"];
              if (variant === "headcount_below") return ["roster-day"];
              if (variant === "geofence_revert_confirm") return ["roster-warehouse"];
              return [];
            })()}
            className="px-md"
          >
            {rosters.map((roster) => (
              <AccordionItem key={roster.id} value={roster.id}>
                <AccordionTrigger>
                  <RosterAccordionSummary
                    roster={roster}
                    siteDefaults={{
                      siteName: site.siteName,
                      address: site.address,
                      latitude: site.latitude,
                      longitude: site.longitude,
                      geofenceRadius: site.geofenceRadius,
                      lateArrivalThreshold: site.lateArrivalThreshold,
                    }}
                    onMoveRoster={openMoveDialog}
                  />
                </AccordionTrigger>
                <AccordionContent>
                  <RosterConfigSection
                    roster={roster}
                    siteDefaults={{
                      siteName: site.siteName,
                      address: site.address,
                      latitude: site.latitude,
                      longitude: site.longitude,
                      geofenceRadius: site.geofenceRadius,
                      lateArrivalThreshold: site.lateArrivalThreshold,
                    }}
                    onChange={handleRosterChange}
                    onRevertField={handleRevertField}
                    onToggleGeofence={handleToggleGeofence}
                    onSave={saveRoster}
                    onDiscard={discardRoster}
                  />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      </div>

      {/* ---------- Move Roster Dialog ---------- */}
      <Dialog
        open={pendingMoveRosterId !== null}
        onOpenChange={(open) => {
          if (!open) closeMoveDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move Roster to another Site</DialogTitle>
            <DialogDescription>
              {movingRoster && (
                <>
                  Move <strong>{movingRoster.name}</strong> away from{" "}
                  <strong>{site.siteName}</strong>. The Roster starts fresh at
                  the destination — all settings reset to the destination
                  Site's defaults, and any custom values for the geofence or
                  late arrival threshold from {site.siteName} are cleared.
                  You can set new custom values after the move.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-xxs">
            <Label>Destination Site</Label>
            <Select
              value={pendingMoveDestination}
              onValueChange={setPendingMoveDestination}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a Site" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(SITE_LABELS)
                  .filter(([key]) => key !== "newtown")
                  .map(([key, label]) => (
                    <SelectItem key={key} value={key}>
                      {label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeMoveDialog}>
              Cancel
            </Button>
            <Button
              variant="default"
              disabled={!pendingMoveDestination}
              onClick={confirmMove}
            >
              Move Roster
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Geofence revert confirm Dialog ---------- */}
      <Dialog
        open={pendingRevertId !== null}
        onOpenChange={(open) => {
          if (!open) setPendingRevertId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Use Site geofence?</DialogTitle>
            <DialogDescription>
              This will discard the Roster's custom latitude, longitude, and
              radius values, and resume inheriting from{" "}
              <strong>{site.siteName}</strong>. Clock validation will use the
              Site's geofence going forward.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingRevertId(null)}
            >
              Cancel
            </Button>
            <Button variant="default" onClick={confirmRevertGeofence}>
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Audit history Dialog ---------- */}
      <Dialog open={auditDrawerOpen} onOpenChange={setAuditDrawerOpen}>
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>Configuration audit history</DialogTitle>
            <DialogDescription>
              Site-level changes and Roster overrides for{" "}
              <strong>{site.siteName}</strong>. Read-only — changes apply
              forward only.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-md max-h-[60vh] space-y-md overflow-y-auto">
            {MOCK_AUDIT.map((entry) => (
              <div
                key={entry.id}
                className="rounded-md border border-greyscale-border p-sm"
              >
                <div className="flex items-center justify-between gap-sm">
                  <p className="text-sm font-semibold text-greyscale-text-title">
                    {entry.field}
                  </p>
                  <span className="text-xs text-greyscale-text-caption">
                    {entry.timestamp}
                  </span>
                </div>
                <p className="text-xs text-greyscale-text-caption mt-xxxs">
                  by {entry.user}
                </p>
                <p className="text-sm text-greyscale-text-body mt-xs">
                  <strong>{entry.oldValue}</strong> →{" "}
                  <strong>{entry.newValue}</strong>
                </p>
                {(entry.updatedRosters.length > 0 ||
                  entry.skippedRosters.length > 0) && (
                  <div className="mt-xs grid gap-xs sm:grid-cols-2 text-xs">
                    <div>
                      <p className="font-semibold text-greyscale-text-title">
                        Updated ({entry.updatedRosters.length})
                      </p>
                      {entry.updatedRosters.length === 0 ? (
                        <p className="text-greyscale-text-caption">None.</p>
                      ) : (
                        <ul className="mt-xxxs space-y-xxxs text-greyscale-text-body">
                          {entry.updatedRosters.map((n) => (
                            <li key={n}>· {n}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-greyscale-text-title">
                        Kept custom ({entry.skippedRosters.length})
                      </p>
                      {entry.skippedRosters.length === 0 ? (
                        <p className="text-greyscale-text-caption">None.</p>
                      ) : (
                        <ul className="mt-xxxs space-y-xxxs text-greyscale-text-body">
                          {entry.skippedRosters.map((n) => (
                            <li key={n}>· {n}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
